<script setup lang="ts">
// Métadonnées de la page Extra
import { LazyStartTrialModal } from '#components'
import { Browser } from '@capacitor/browser'
import { Purchases } from '@revenuecat/purchases-capacitor'
import { toRaw } from 'vue'

const userStore = useUserStore()
const modalStore = useModalStore()
const baseStore = useBaseStore()
const overlay = useOverlayWithAd()
const toast = useToast()
const localePath = useLocalePath()
const config = useRuntimeConfig()

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    fr: '/extra',
  },
})

definePageMeta({
  name: 'ExtraPage',
})

// Head configuration
useHead({
  title: 'L\'Extra: Glouton en mode turbo',
  meta: [
    { name: 'description', content: 'Économisez plsu de 4000$ par année grâce à une approche intelligente de vos achats alimentaires.' },
    { property: 'og:title', content: 'L\'Extra: Glouton en mode turbo' },
    { property: 'og:description', content: 'Économisez plus de 4000$ par année grâce à une approche intelligente de vos achats alimentaires.' },
    { property: 'og:image', content: 'https://glouton.b-cdn.net/site/images/social.jpg' },
  ],
})

// Reactive data
const selectedPricing = ref('yearly')
const promoWarning = ref<any>(false)
const orderLoading = ref(false)
const orderCompleted = ref(false)
const monthlySku = ref<any>(false)
const yearlySku = ref<any>(false)
const publicKey = ref<string>('')

// Helper function to format price
function formatPrice(price: string | number): string {
  const numPrice = typeof price === 'string' ? Number.parseFloat(price) : price
  return `${Math.round(numPrice)}$`
}

// Promo data
const promo = computed(() => baseStore.promos)

// Show promo pricing only if shouldShowPromos and promo exists and yearly is selected
const showPromoPricing = computed(() => baseStore.shouldShowPromos && promo.value && selectedPricing.value === 'yearly')

// Promo pricing details for annual
const promoPricing = computed(() => {
  const oldPrice = promo.value?.old_price ? Number.parseFloat(promo.value.old_price.toString()) : 10
  const percentage = promo.value?.percentage ? Number.parseFloat(promo.value.percentage) : 40

  return {
    annualOldPrice: formatPrice(120),
    annualNewPrice: formatPrice(oldPrice * (1 - percentage / 100)),
    monthlyPrice: formatPrice(oldPrice * (1 - percentage / 100) / 12),
    percentage: Math.round(percentage),
  }
})

// Formatage de la date de fin d'essai
const formattedTrialEndDate = computed(() => {
  if (!userStore.user?.trial_ends_at)
    return ''

  const date = new Date(userStore.user.trial_ends_at)
  const day = date.getDate()
  const month = date.toLocaleDateString('fr-FR', { month: 'long' })

  return `${day} ${month}`
})

async function openStartTrialModal() {
  // Vérifier si l'utilisateur est connecté
  if (!userStore.user) {
    toast.add({
      title: 'Connexion requise',
      color: 'error',
      description: 'Vous devez être connecté pour activer un essai gratuit.',
      actions: [{
        label: 'Se connecter',
        color: 'secondary',
        variant: 'outline',
        onClick: () => {
          modalStore.openLoginModal()
        },
      }, {
        label: 'Créer un compte',
        color: 'secondary',
        variant: 'solid',
        to: localePath({ name: 'RegisterPage' }),
      }],
      icon: 'i-lucide-alert-circle',
    })
    return
  }

  const modal = overlay.create(LazyStartTrialModal)
  modal.open()
}

async function handleOrder() {
  // Vérifier si l'utilisateur est connecté
  if (!userStore.user) {
    toast.add({
      title: 'Connexion requise',
      color: 'error',
      description: 'Vous devez être connecté pour commander l\'Extra.',
      actions: [{
        label: 'Se connecter',
        color: 'secondary',
        variant: 'outline',
        onClick: () => {
          modalStore.openLoginModal()
        },
      }, {
        label: 'Créer un compte',
        color: 'secondary',
        variant: 'solid',
        to: localePath({ name: 'RegisterPage' }),
      }],
      icon: 'i-lucide-alert-circle',
    })
    return
  }

  // Vérifier si l'utilisateur n'est pas déjà Extra (ou en trial)
  if (userStore.user.extra == 1 && userStore.user.extra_active_trial == 0) {
    toast.add({
      title: 'Déjà abonné',
      color: 'info',
      description: 'Vous avez déjà un abonnement Extra actif.',
      icon: 'i-lucide-info',
    })
    return
  }

  // Préparer les paramètres de navigation
  const queryParams: any = {}
  // Si une promo est active, ajouter le code promo
  if (baseStore.shouldShowPromos && promo.value?.code) {
    queryParams.code = promo.value.code
  }

  // Gérer selon la plateforme
  if (baseStore.platform === 'web') {
    // Rediriger vers ActivateExtraPage pour le web (Stripe)
    navigateTo(localePath({ name: 'ActivateExtraPage', query: queryParams }))
  }
  else if ((baseStore.platform === 'ios' || baseStore.platform === 'android') && showPromoPricing.value && promo.value?.code) {
    // Open promo direct activation page with email and code
    const promoUrl = `https://www.glouton.app/fr/promo-extra?acc=${encodeURIComponent(userStore.user.email)}&code=${encodeURIComponent(promo.value.code)}`
    await Browser.open({ url: promoUrl, toolbarColor: '#F37A4E' })
  }
  else if (baseStore.platform === 'ios' || baseStore.platform === 'android') {
    // Gérer avec RevenueCat pour mobile
    await handleMobileSubscription()
  }
  else {
    // Fallback vers le web
    navigateTo(localePath({ name: 'ActivateExtraPage', query: queryParams }))
  }
}

// Fonction pour initialiser RevenueCat
async function startRevenueCat() {
  try {
    // const { Purchases } = await import('@revenuecat/purchases-capacitor')

    // Définir la clé publique selon la plateforme
    if (baseStore.platform === 'ios') {
      publicKey.value = 'appl_fBOUhyJnNVqmgSnMXXFDMHnzzgT'
    }
    else {
      publicKey.value = 'goog_xOMlXfeOJVPDtTZaMSAvEafIvYc'
    }

    // Always configure RevenueCat first
    await Purchases.configure({
      apiKey: publicKey.value,
    })

    // Then set the user ID if user is logged in
    if (userStore.user?.id) {
      await setRevenueCatUserID()
    }

    console.log('RevenueCat configured successfully')
    await loadRevenueCatProducts()
  }
  catch (error) {
    console.error('Error configuring RevenueCat:', error)
  }
}

// Fonction pour définir l'ID utilisateur RevenueCat
async function setRevenueCatUserID() {
  try {
    if (userStore.user?.id) {
      // const { Purchases } = await import('@revenuecat/purchases-capacitor')
      const userId = String(userStore.user.id)

      await Purchases.logIn({ appUserID: userId })
      console.log('RevenueCat user ID set successfully:', userId)
    }
  }
  catch (error) {
    console.error('Error setting RevenueCat user ID:', error)
  }
}

// Fonction pour charger les produits RevenueCat
async function loadRevenueCatProducts() {
  try {
    // const { Purchases } = await import('@revenuecat/purchases-capacitor')
    console.log('Loading products...')
    console.log('Platform:', baseStore.platform)

    // Vérifier si RevenueCat est configuré
    const customerInfo = await Purchases.getCustomerInfo()
    console.log('RevenueCat customer info:', customerInfo)

    const offerings = await Purchases.getOfferings()
    console.log('Offerings received:', offerings)

    if (offerings && offerings.current) {
      const packages = offerings.current.availablePackages
      console.log('Available packages:', packages)

      if (packages.length > 0) {
        console.log('Products found:')
        packages.forEach((pkg) => {
          console.log('Package:', pkg.identifier, pkg)
          if (pkg.identifier === '$rc_monthly') {
            monthlySku.value = pkg
            console.log('Monthly SKU set:', pkg)
          }
          else if (pkg.identifier === '$rc_annual') {
            yearlySku.value = pkg
            console.log('Yearly SKU set:', pkg)
          }
          else {
            // Si ce n'est pas monthly, considérer comme yearly par défaut
            if (!yearlySku.value) {
              yearlySku.value = pkg
              console.log('Yearly SKU set as fallback:', pkg)
            }
          }
        })
      }
      else {
        console.log('No available packages found.')
      }
    }
    else {
      console.log('No offerings available.')
    }
  }
  catch (error) {
    console.error('Error loading RevenueCat products:', error)
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      platform: baseStore.platform,
    })
  }
}

// Fonction pour gérer les abonnements mobiles avec RevenueCat
async function handleMobileSubscription() {
  try {
    // const { Purchases } = await import('@revenuecat/purchases-capacitor')

    orderLoading.value = true

    // Vérifier que l'utilisateur est bien connecté avant l'achat
    if (!userStore.user?.id) {
      throw new Error('Utilisateur non connecté')
    }

    // S'assurer que l'ID utilisateur est défini dans RevenueCat
    await setRevenueCatUserID()

    // Vérifier l'ID utilisateur actuel dans RevenueCat
    const customerInfo = await Purchases.getCustomerInfo()
    console.log('Current RevenueCat user ID:', (customerInfo as any).originalAppUserId)

    // Sélectionner le package selon le pricing choisi
    const selectedSku = selectedPricing.value === 'yearly' ? yearlySku.value : monthlySku.value

    console.log('Attempting to purchase package with identifier:', selectedSku.identifier)
    console.log('User ID before purchase:', userStore.user.id)
    console.log('Platform:', baseStore.platform)

    // Effectuer l'achat
    const purchaseResult = await Purchases.purchasePackage({ aPackage: toRaw(selectedSku) })
    console.log('Purchase successful:', purchaseResult)

    // Gérer les informations après achat
    if (purchaseResult.customerInfo) {
      console.log('Purchase is valid')
      orderCompleted.value = true
      orderLoading.value = false

      // Mettre à jour l'utilisateur
      if (userStore.user) {
        userStore.user.extra = 1
        userStore.user.extra_active_trial = 0
        await userStore.setUser(userStore.user)
      }

      toast.add({
        title: 'Achat réussi!',
        description: 'Vous êtes maintenant Extra et vous pouvez profiter de tous les avantages de Glouton, sans publicité. Merci de votre support! 🙌',
        icon: 'i-lucide-gem',
        color: 'success',
        duration: 10000,
      })
    }

    console.log('Customer info:', purchaseResult.customerInfo)
  }
  catch (error: any) {
    orderLoading.value = false

    console.error('Error during purchase:', error)
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      userCancelled: error.userCancelled,
      platform: baseStore.platform,
    })

    toast.add({
      title: 'Achat annulé!',
      description: `L'achat n'a pu être complété. La boutique a donné l'erreur suivante: ${error.message || ''}`,
      icon: 'i-lucide-alert-circle',
      color: 'error',
    })
  }
}

// Pricing options for UTabs
const pricingOptions = [
  {
    label: 'Mensuel',
    value: 'monthly',
    discount: null,
  },
  {
    label: 'Annuel',
    value: 'yearly',
    discount: '-28%',
  },
]

// Features data
const includedFeatures = [
  '<b>Suivez les prix</b> de vos produits préférés',
  'Sauvegardez, classez et annotez <b>un nombre illimité de recettes</b>',
  'Importez des recettes de n\'importe quel site Web',
  'Les <b>recettes les plus avantageuse</b> selon les rabais avec L\'économiseur',
  'Organisez vos menus avec le Planificateur',
  'Filtrez les rabais <b>par allée</b> dans les circulaires et les meilleurs rabais',
  'Classez automatiquement votre liste d\'épicerie <b>par allée</b>',
  '<b>Rabais exclusifs</b> chez nos partenaires',
  'Expérience 100% <b>sans publicité</b>',
  'Et bien plus encore...',
]

const tiers = [
  {
    name: 'Extra',
    label: 'Extra',
    href: '#',
    priceMonthly: 5,
  },
  {
    name: 'Free',
    label: 'Gratuit',
    href: '#',
    priceMonthly: 0,
  },
]

const { data: discountCountsData } = await useAsyncData(
  'extra-discounts-counts',
  () => $fetch<{
    success: number
    discounts: unknown[]
    count_free: number
    count_extra: number
  }>(`${config.public.apiUrl}/extra-discounts/fetch-discounts`, {
    query: {
      for_all_members: false,
      only_extra_members: false,
    },
  }),
)

type ComparisonFeature = {
  name: string
  to?: string
  tiers: Record<string, boolean | string>
}

type ComparisonSection = {
  name: string
  to?: string
  features: ComparisonFeature[]
}

const sections = computed((): ComparisonSection[] => {
  const countFree = discountCountsData.value?.count_free
  const countExtra = discountCountsData.value?.count_extra
  const hasCounts = typeof countFree === 'number' && typeof countExtra === 'number'
  const freeLabel = hasCounts ? `${countFree} (limité)` : 'Limité'
  const extraLabel = hasCounts ? `${countFree + countExtra} (tous)` : 'Complet'

  return [
    {
      name: 'Fonctionnalités',
      features: [
        { name: 'Cherchez des aubaines ou des recettes par mot clé ou ingrédient', tiers: { Free: true, Extra: true } },
        { name: 'Filtrez les recettes par catégories, thèmes ou restrictions alimentaires', to: 'RecipesPage', tiers: { Free: true, Extra: true } },
        { name: 'Réduisez le gaspillage et économisez avec le vide-frigo', tiers: { Free: true, Extra: true } },
        { name: 'Trouvez les meilleures aubaines', to: 'BestItemsPage', tiers: { Free: true, Extra: true } },
        { name: 'Filtrez les rabais par allée d\'épicerie dans les circulaires et les meilleurs rabais', tiers: { Free: false, Extra: true } },
        { name: 'Annotez toutes les recettes, y compris celles importées', tiers: { Free: false, Extra: true } },
        { name: 'Profitez de Glouton sans publicité', tiers: { Free: false, Extra: true } },
        { name: 'Rabais Extra chez nos partenaires', to: 'ExtraDiscountsPage', tiers: { Free: freeLabel, Extra: extraLabel } },
      ],
    },
    {
      name: 'Liste d\'épicerie',
      to: 'ShoppingListPage',
      features: [
        { name: 'Accès à la liste d\'épicerie', tiers: { Free: true, Extra: true } },
        { name: 'Classez automatiquement votre liste d\'épicerie par allée', tiers: { Free: false, Extra: true } },
      ],
    },
    {
      name: 'Produits suivis',
      to: 'TrackedIngredientsPage',
      features: [
        { name: 'Suivez vos produits préférés pour ne manquer aucun rabais chaque semaine', tiers: { Free: false, Extra: true } },
        { name: 'Groupez et affichez les rabais sur vos produits préférés sur une seule page', tiers: { Free: false, Extra: true } },
        { name: 'Recevez par courriel un résumé des rabais sur vos produits suivis chaque mercredi', tiers: { Free: false, Extra: true } },
      ],
    },
    {
      name: 'L\'économiseur',
      to: 'MoneySaverPage',
      features: [
        { name: 'Trouvez les recettes les plus avantageuses à partir des aubaines et de votre liste d\'épicerie', tiers: { Free: false, Extra: true } },
        { name: 'Combinez les rabais avec les ingrédients que vous avez déjà pour encore plus d\'économies', tiers: { Free: false, Extra: true } },
        { name: 'Filtrez les recettes par catégories, thèmes et restrictions alimentaires', tiers: { Free: false, Extra: true } },
      ],
    },
    {
      name: 'Boîte de recettes',
      to: 'RecipeBoxPage',
      features: [
        { name: 'Limite de recettes sauvegardées', tiers: { Free: '25', Extra: 'Illimité' } },
        { name: 'Classez des recettes dans des listes personnalisées', tiers: { Free: false, Extra: true } },
        { name: 'Importez des recettes de n\'importe quel site Web', tiers: { Free: false, Extra: true } },
      ],
    },
    {
      name: 'Planificateur de menus',
      to: 'MealPlannerPage',
      features: [
        { name: 'Accès au planificateur de menus: un calendrier pour organiser les repas', tiers: { Free: false, Extra: true } },
        { name: 'Importez des recettes de n\'importe quel site Web', tiers: { Free: false, Extra: true } },
      ],
    },
  ]
})

const faqs = [
  {
    label: 'Comment fonctionne la période d\'essai gratuite?',
    content: 'Nous offrons une période d\'essai gratuite de 14 jours pour vous familiariser avec l\’Extra et aucune carte de crédit n\'est exigée pour en profiter.<br><br>Vous appréciez Glouton? Vous pouvez acheter un abonnement avant la fin de votre période d\'essai.<br><br>L\'Extra n’est pas pour vous? Puisque vous n\'avez pas entré de carte de crédit, votre carte ne sera pas chargée à l\'expiration de l\'essai et votre compte passera automatiquement en mode gratuit.',
  },
  {
    label: 'Quels sont les modes de paiement acceptés?',
    content: 'Vous pouvez payer par carte de crédit (Visa, MasterCard ou Amex). Votre abonnement sera renouvelé automatiquement chaque mois ou année jusqu\'à l\'annulation de votre abonnement.',
  },
  {
    label: 'Est-ce qu\'il y a un engagement?',
    content: 'Non! Vous pouvez annuler votre abonnement n\'importe quand, votre abonnement restera valide pour la période déjà payée puis se terminera automatiquement ensuite.',
  },
  {
    label: 'À quelle quantité d\'économies je peux m\'attendre?',
    content: 'Nos tests ont montré qu\'il est tout à fait possible d\'atteindre 4000$ d\'économies par année pour une famille de deux adultes et deux enfants, en utilisant L\'économiseur avec au moins 2 supermarchés à votre profil. Si vous utilisez déjà très rigoureusement les circulaires, vos économies risquent d\'être moins élevées, mais vous gagnerez en variété dans vos menus et sauverez du temps précieux. Avec les économies, c\'est comme si votre abonnement se payait lui-même.',
  },
  {
    label: 'Quelle est votre politique de remboursement?',
    content: 'Nous offrons une période d\'essai gratuite et sans carte de crédit requise pour que vous puissiez prendre le temps de voir si Glouton vous convient. Une fois acheté, il n\'est alors pas possible de procéder à un remboursement.',
  },
  {
    label: 'Est-ce que je dois m\'abonner plusieurs fois à l\'Extra pour en profiter sur la version web et dans l\'app mobile?',
    content: 'Non! L\'Extra est lié à votre compte Glouton et vous pourrez donc profiter de tous les avantages autant dans la version web que dans l\'application vec un seul abonnement.',
  },
  {
    label: 'J\'ai d\'autres questions à propos de Glouton et l\'abonnement Extra?',
    content: 'Vous pouvez nous contacter sur notre <i>live chat</i> accessible dans le menu. Nous répondons habituellement en quelques minutes.',
  },
]

// Watch for user changes to update RevenueCat user ID
watch(() => userStore.user, async (newUser, oldUser) => {
  if (baseStore.platform === 'ios' || baseStore.platform === 'android') {
    if (newUser?.id && newUser.id !== oldUser?.id) {
      // User logged in or changed
      await setRevenueCatUserID()
    }
  }
}, { immediate: false })

// Check promo on mount and initialize RevenueCat
const route = useRoute()

function openPromoModalFromQuery() {
  if (typeof route.query.code !== 'string' || !route.query.code.trim())
    return
  nextTick(() => {
    modalStore.openPromoBannerModal()
  })
}

onMounted(async () => {
  try {
    const promoCode = typeof route.query.code === 'string' ? route.query.code.trim() : ''
    const response = await $fetch(`${config.public.apiUrl}/home/check-promos`, {
      query: promoCode ? { code: promoCode } : undefined,
    })
    if (response.success === 1) {
      promoWarning.value = response.data
    }
  }
  catch (err) {
    console.error('Error checking promos:', err)
  }

  if (userStore.authChecked) {
    openPromoModalFromQuery()
  }

  // Initialize RevenueCat for mobile platforms
  if (baseStore.platform === 'ios' || baseStore.platform === 'android') {
    await startRevenueCat()
  }
})

watch(() => userStore.authChecked, (checked) => {
  if (checked)
    openPromoModalFromQuery()
})
</script>

<template>
  <div id="ExtraPage" class="relative">
    <!-- Background gradient -->
    <div class="absolute inset-0 bg-gradient-to-br from-tomate/5 via-bleuet/5 to-sauge/5" />

    <!-- Header section -->
    <div class="bg-sorbet relative overflow-hidden">
      <!-- Background decorative elements -->
      <div class="absolute inset-0">
        <div class="absolute top-0 left-1/4 w-72 h-72 bg-tomate/10 rounded-full blur-3xl" />
        <div class="absolute top-1/2 right-1/4 w-96 h-96 bg-bleuet/10 rounded-full blur-3xl" />
        <div class="absolute bottom-0 left-1/3 w-80 h-80 bg-sauge/10 rounded-full blur-3xl" />
      </div>

      <div class="pt-8 sm:pt-16 lg:pt-20 relative">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
          <div class="text-center">
            <h2 class="text-3xl font-extrabold text-poivre sm:text-4xl lg:text-5xl font-title bg-gradient-to-r from-tomate-600 via-bleuet-600 to-sauge-600 bg-clip-text text-transparent pb-2">
              Maximisez vos économies de temps et d'argent
            </h2>
            <p class="mt-4 text-lg text-poivre-600 max-w-3xl mx-auto leading-relaxed">
              Les membres Extra peuvent économiser plus de <span class="font-semibold text-tomate-600">4000$ par année</span> grâce à une meilleure planification et en optimisant leur utilisation des spéciaux.
            </p>
          </div>

          <PromoBanner class="mt-6" message-type="message_home" />
        </div>
      </div>

      <!-- Pricing card -->
      <div class="mt-8 bg-white pb-16 xl:mt-12 sm:pb-20 xl:pb-28">
        <div class="relative">
          <div class="absolute inset-0 h-1/2 bg-gradient-to-b from-sorbet to-white" />
          <div class="relative max-w-7xl mx-auto px-4 sm:px-6 xl:px-8">
            <div class="flex flex-col-reverse max-w-lg mx-auto xl:rounded-2xl border-1 shadow-2xl border-tomate-400 xl:max-w-none xl:flex xl:flex-row xl:overflow-hidden bg-white/80 backdrop-blur-sm rounded-lg overflow-hidden">
              <div class="flex-1 bg-white/90 backdrop-blur-sm px-6 sm:pl-6 sm:pr-16 xl:pl-8 xl:pr-16 pb-12 pt-8 sm:py-8 xl:py-8">
                <UBadge color="tertiary" class="mb-4" label="1% aux Banques alimentaires du Québec" icon="i-mdi-hand-heart-outline" variant="subtle" size="lg" :ui="{ base: 'rounded-full py-2 px-3' }" />
                <h3 class="text-2xl text-poivre-800 sm:text-3xl font-title bg-gradient-to-r from-tomate-600 to-bleuet-600 bg-clip-text text-transparent">
                  Petit prix, grosses économies
                </h3>
                <p class="mt-6 text-base text-poivre-500 leading-relaxed">
                  La plupart de nos fonctionnalités sont utilisables gratuitement avec des limitations. L'abonnement Extra enlève ces limitations et donne accès à des options supplémentaires, en plus de retirer toutes les publicités.
                </p>
                <div class="mt-8">
                  <div class="flex items-center mb-6">
                    <h4 class="flex-shrink-0 pr-4 bg-white text-sm tracking-wider font-semibold uppercase text-tomate-400">
                      Avec l'extra
                    </h4>
                    <div class="flex-1 border-t-1 border-gray-200" />
                  </div>
                  <div class="mt-8 grid grid-cols-1 xl:grid-cols-2 xl:grid-flow-col xl:grid-rows-5 gap-2">
                    <div v-for="feature in includedFeatures" :key="feature" class="flex items-start">
                      <div class="flex-shrink-0">
                        <UIcon name="i-mdi-check" class="h-5 w-5 text-sauge" />
                      </div>
                      <p class="ml-3 text-base text-poivre-700" v-html="feature" />
                    </div>
                  </div>
                </div>
              </div>
              <div class="py-6 px-6 sm:py-8 text-center bg-sel/60 xl:flex xl:flex-col xl:justify-center relative xl:w-1/3 2xl:w-1/3 backdrop-blur-sm">
                <!-- Bloc pour utilisateur déjà Extra (pas en essai) -->
                <div v-if="userStore.user && userStore.user.extra == 1 && userStore.user.extra_active_trial == 0" class="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-poivre-200/50 shadow-lg text-center">
                  <div class="flex flex-col items-center justify-center mb-4">
                    <UIcon name="i-mdi-check-circle" class="size-9 text-green-600 mr-2" />
                    <div>
                      <div class="text-xl font-bold text-sauge-800">
                        Abonnement Extra actif
                      </div>
                      <div class="text-sm text-sauge-600">
                        Vous profitez de tous les avantages de l'abonnement Extra. Merci de votre support!
                      </div>
                    </div>
                  </div>
                  <UButton
                    color="secondary"
                    variant="outline"
                    size="md"
                    icon="i-mdi-cog"
                    :to="localePath({ name: 'UserProfilePage' })"
                  >
                    Gérer mon abonnement
                  </UButton>
                </div>

                <!-- Bloc pour utilisateur pas Extra ou en essai -->
                <div v-else class="relative space-y-4">
                  <!-- Pricing Tabs -->
                  <UTabs
                    v-model="selectedPricing"
                    :items="pricingOptions.map(option => ({
                      label: option.label,
                      value: option.value,
                      discount: option.discount,
                    }))"
                    color="primary"
                    size="xl"
                    :ui="{ list: 'bg-white' }"
                    class="mb-0"
                  >
                    <template #default="{ item }">
                      <div class="flex items-center gap-2">
                        <span>{{ item.label }}</span>
                        <UBadge
                          v-if="item.discount && item.value === selectedPricing && !promo"
                          :label="item.discount"
                          color="white"
                          variant="outline"
                          size="md"
                        />
                        <UBadge
                          v-else-if="item.discount && !promo"
                          :label="item.discount"
                          color="primary"
                          variant="soft"
                          size="md"
                        />
                      </div>
                    </template>
                  </UTabs>

                  <!-- Price Display -->
                  <div class="bg-white/80 backdrop-blur-sm rounded-2xl mt-3 xl:mt-4 p-6 border border-poivre-200/50 shadow-lg">
                    <div v-if="selectedPricing === 'monthly'">
                      <div
                        class="flex items-center justify-center space-x-2"
                      >
                        <div class="text-4xl font-bold bg-gradient-to-r from-tomate-600 to-bleuet-600 bg-clip-text text-transparent mb-2">
                          $14
                        </div>
                        <div class="text-sm text-poivre-600 font-medium">
                          /mois
                        </div>
                      </div>
                    </div>

                    <div v-else-if="selectedPricing === 'yearly'" class="">
                      <!-- Promo pricing display -->
                      <div v-if="showPromoPricing" class="text-center bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-3">
                        <div class="flex items-center justify-center space-x-4 mb-2">
                          <span class="text-2xl text-gray-500 line-through">{{ promoPricing.annualOldPrice }}</span>
                          <span class="text-5xl font-bold text-orange-600">{{ promoPricing.annualNewPrice }}</span>
                          <span class="text-sm text-gray-500">par an</span>
                        </div>
                        <div class="space-y-2 flex flex-col items-center">
                          <div class="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
                            <UIcon name="i-mdi-tag" class="w-4 h-4 mr-1" />
                            Économisez {{ promoPricing.percentage }}%
                          </div>
                          <p class="text-base text-green-600 animate-bounce font-bold mt-2">
                            Revient à {{ promoPricing.monthlyPrice }} par mois!
                          </p>
                          <p class="text-xs text-poivre-500 mt-1">
                            Offre disponible jusqu'au {{ promo.end_date ? new Date(promo.end_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }) : '31 décembre 2024' }}, sur l'abonnement annuel seulement.
                          </p>
                        </div>
                      </div>
                      <!-- Regular pricing display -->
                      <div v-else class="flex items-center justify-center space-x-2">
                        <div class="text-4xl font-bold bg-gradient-to-r from-tomate-600 to-bleuet-600 bg-clip-text text-transparent mb-2">
                          120$
                        </div>
                        <div class="text-sm text-poivre-600 font-medium">
                          /année
                        </div>
                      </div>
                      <div v-if="!showPromoPricing" class="text-xs text-green-600 font-semibold">
                        Économisez 28% avec l'annuel!
                      </div>
                    </div>
                    <UButton
                      color="primary"
                      size="lg"
                      class="mt-3"
                      :loading="orderLoading"
                      :disabled="orderLoading"
                      icon="i-lucide-credit-card"
                      label="Commander"
                      @click="handleOrder"
                    />
                  </div>

                  <!-- Trial Section -->
                  <div class="bg-gradient-to-br from-sauge-50 to-sauge-100/50 rounded-2xl p-4 border border-sauge-200/50 shadow-lg">
                    <div class="flex items-center justify-center mb-2">
                      <UIcon name="i-mdi-gift" class="w-5 h-5 text-sauge-600 mr-2" />
                      <h4 class="text-base font-bold text-sauge-800">
                        Essai gratuit
                      </h4>
                    </div>

                    <!-- État de l'essai selon l'utilisateur -->
                    <div v-if="userStore.user && userStore.user.extra == 1 && userStore.user.extra_active_trial == 1" class="text-center mb-3">
                      <p class="text-xs text-sauge-700 mb-2 leading-tight">
                        Votre essai est présentement en cours et se termine le <span class="font-semibold">{{ formattedTrialEndDate }}</span>.
                      </p>
                      <p class="text-xs text-sauge-700 leading-tight">
                        Commandez avant cette date pour ne pas perdre vos avantages.
                      </p>
                    </div>
                    <div v-else-if="userStore.user && userStore.user.trial_status > 0" class="text-center mb-3">
                      <p class="text-xs text-sauge-700 leading-tight">
                        Votre compte a déjà profité d'un essai gratuit. Vous avez des questions avant de commander? Contactez notre support.
                      </p>
                    </div>
                    <div v-else class="text-center mb-3">
                      <p class="text-xs text-sauge-700 leading-tight">
                        Testez toutes les fonctionnalités Extra pendant 14 jours sans risque ni engagement.
                      </p>
                    </div>

                    <!-- Security badges -->
                    <div v-if="!userStore.user || (userStore.user && userStore.user.trial_status === 0)" class="mb-3 inline-flex items-center justify-center px-2 py-1 bg-white/40 rounded-md">
                      <UIcon name="i-mdi-credit-card-off" class="size-4 text-sauge-500 mr-1 block" />
                      <div class="text-xs text-sauge-600">
                        Aucune carte de crédit requise
                      </div>
                    </div>

                    <!-- Bouton selon l'état -->
                    <div class="flex justify-center">
                      <UButton
                        v-if="!userStore.user || (userStore.user && userStore.user.trial_status === 0)"
                        color="secondary"
                        variant="outline"
                        size="sm"
                        class="flex py-2 text-sm font-semibold border-sauge-300 text-sauge-700 hover:bg-sauge-100 transition-colors"
                        icon="i-mdi-play-circle"
                        @click="openStartTrialModal"
                      >
                        Activer l'essai gratuit
                      </UButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Comparison table -->
    <div class="relative bg-sel">
      <!-- Background decorative elements -->

      <div class="relative">
        <div class="text-center mb-12 pt-12">
          <h2 class="text-3xl font-extrabold text-center text-poivre-800 sm:text-4xl font-title bg-gradient-to-r from-bleuet-600 to-sauge-600 bg-clip-text text-transparent">
            Comparez les avantages
          </h2>
          <p class="mt-4 text-lg text-poivre-600 max-w-2xl px-2 mx-auto">
            Découvrez la différence entre l'abonnement gratuit et l'Extra
          </p>
          <UButton
            color="secondary"
            size="md"
            class="mt-4"
            icon="i-mdi-play"
            @click="modalStore.openExtraVideoModal()"
          >
            Voir la vidéo
          </UButton>
        </div>

        <div class="max-w-7xl mx-auto bg-white/80 backdrop-blur-sm xl:rounded-3xl border border-poivre-200/50 shadow-xl py-3 xl:py-16 sm:px-6 lg:px-8">
          <div class="block">
            <table class="w-full h-px table-fixed">
              <thead>
                <tr>
                  <th class="pb-4 px-6 text-sm font-medium text-poivre-800 text-left w-[64%]" scope="col">
                    <span class="font-semibold">Plans</span>
                  </th>
                  <th
                    v-for="tier in tiers"
                    :key="tier.name"
                    class="w-[18%] pb-3 px-1 text-lg leading-6 text-left pt-3"
                    :class="[tier.priceMonthly > 0 ? 'relative overflow-hidden font-bold rounded-t-lg shadow-lg text-poivre-900' : 'font-medium text-poivre-800 bg-poivre-50 rounded-t-lg']"
                    :style="tier.priceMonthly > 0 ? { background: 'linear-gradient(135deg, #ddc990 0%, #ebe0b0 40%, #f3ecd4 50%, #ebe0b0 60%, #ddc990 100%)' } : undefined"
                    scope="col"
                  >
                    <div class="flex items-center justify-center">
                      <UIcon v-if="tier.priceMonthly > 0" name="i-mdi-star" class="mr-2 size-5 text-tomate-600/90 shrink-0 hidden md:block" />
                      <span class="font-bold text-poivre-700">{{ tier.label }}</span>
                    </div>
                    <div v-if="tier.priceMonthly > 0" class="extra-tier-shine absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent pointer-events-none" />
                  </th>
                </tr>
              </thead>
              <tbody class="border-t border-poivre-200 divide-y divide-poivre-200">
                <tr>
                  <th class="py-6 px-6 text-sm font-medium text-poivre-800 text-left align-middle" scope="row">
                    Prix
                  </th>
                  <td v-for="tier in tiers" :key="tier.name" class="h-full py-4 lg:py-6 px-1 lg:px-2 align-middle text-center">
                    <div v-if="tier.priceMonthly === 0">
                      <p>
                        <span class="text-xl block lg:inline lg:text-4xl font-extrabold text-poivre-800 text-center">${{ tier.priceMonthly }}</span>
                        {{ ' ' }}
                        <span class="block lg:inline text-sm lg:text-base font-medium text-poivre-500 text-center">/mo</span>
                      </p>
                    </div>
                    <div v-else-if="selectedPricing === 'monthly'">
                      <p>
                        <span class="block lg:inline text-xl text-center lg:text-4xl font-extrabold text-poivre-800">$14</span>
                        {{ ' ' }}
                        <span class="block lg:inline text-sm lg:text-base font-medium text-poivre-500 text-center">/mo</span>
                      </p>
                    </div>
                    <div v-else>
                      <p>
                        <span class="text-xl block lg:inline lg:text-4xl font-extrabold text-poivre-800 text-center">$120</span>
                        {{ ' ' }}
                        <span class="block lg:inline text-sm lg:text-base font-medium text-poivre-500">/année</span>
                      </p>
                    </div>
                  </td>
                </tr>
                <template v-for="section in sections" :key="section.name">
                  <tr>
                    <th class="bg-poivre-50 py-2 px-3 lg:py-3 lg:pl-6 text-sm font-medium text-poivre-800 text-left uppercase" colspan="3" scope="colgroup">
                      <span class="inline-flex items-center gap-1.5">
                        {{ section.name }}
                        <NuxtLink
                          v-if="section.to"
                          :to="localePath({ name: section.to })"
                          class="inline-flex items-center text-primary hover:text-tomate-500 normal-case leading-none"
                          :aria-label="`Voir : ${section.name}`"
                        >
                          <UIcon name="i-mdi-arrow-right-thin" class="size-4" />
                        </NuxtLink>
                      </span>
                    </th>
                  </tr>
                  <tr v-for="feature in section.features" :key="feature.name">
                    <th class="py-2 pl-3 pr-1 lg:py-5 lg:px-6 text-sm lg:text-base font-normal text-poivre-600 text-left" scope="row">
                      {{ feature.name }}<NuxtLink
                        v-if="feature.to"
                        :to="localePath({ name: feature.to })"
                        class="ml-1 inline-flex items-center align-middle text-primary hover:text-tomate-500 leading-none"
                        :aria-label="`Voir : ${feature.name}`"
                      >
                        <UIcon name="i-mdi-arrow-right-thin" class="size-4" />
                      </NuxtLink>
                    </th>
                    <td v-for="tier in tiers" :key="tier.name" class="py-5 px-2 lg:px-3 text-center">
                      <span v-if="typeof (feature.tiers as any)[tier.name] === 'string'" class="text-sm lg:text-base text-poivre" :class="[tier.priceMonthly > 0 ? 'font-semibold' : '']">
                        {{ (feature.tiers as any)[tier.name] }}
                      </span>
                      <span v-else>
                        <UIcon
                          v-if="(feature.tiers as any)[tier.name] === true && tier.priceMonthly > 0"
                          name="i-mdi-check-bold"
                          class="size-6 text-green-500"
                          aria-hidden="true"
                        />
                        <UIcon
                          v-else-if="(feature.tiers as any)[tier.name] === true"
                          name="i-mdi-check"
                          class="size-6 text-green-500 "
                        />
                        <UIcon
                          v-else
                          name="i-mdi-close"
                          class="size-6 text-red-400 "
                        />
                      </span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- FAQ section -->
      <div class="px-4 pb-12 mx-auto max-w-7xl sm:px-6 lg:px-8 mb-8">
        <div class="max-w-4xl mx-auto">
          <div class="text-center mb-12 pt-12">
            <h2 class="text-3xl font-extrabold text-center text-poivre-800 sm:text-4xl font-title bg-gradient-to-r from-bleuet-600 to-sauge-600 bg-clip-text text-transparent">
              Questions fréquentes
            </h2>
            <p class="mt-4 text-lg text-poivre-600 max-w-2xl mx-auto">
              Trouvez rapidement les réponses à vos questions les plus courantes
            </p>
          </div>

          <div class="bg-white/80 backdrop-blur-sm rounded-3xl border border-poivre-200/50 shadow-xl p-8">
            <div class="space-y-6 divide-y divide-poivre-200">
              <UAccordion
                :items="faqs"
                class="divide-y divide-poivre-100"
                :ui="{ trigger: 'text-base', body: 'text-base' }"
              >
                <template #content="{ item }">
                  <p class="pb-3.5" v-html="item.content" />
                </template>
              </UAccordion>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes extra-tier-shine {
  0% {
    transform: translateX(-100%) skewX(-12deg);
  }
  100% {
    transform: translateX(200%) skewX(-12deg);
  }
}

.extra-tier-shine {
  animation: extra-tier-shine 3s ease-in-out infinite;
}
</style>
